import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { DropdownOption } from 'src/app/components/common/common-dropdown/common-dropdown.component.model';
import { Client, ContractorTypeSearchOptions } from 'src/app/models';
import {
  WorkflowContextService,
  WorkflowService,
  ClientService,
  SystemService,
  ContractorService
} from 'src/app/services';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';

@Component({
  selector: 'app-test-workflow-modal',
  templateUrl: './test-workflow-modal.component.html',
  styleUrls: ['./test-workflow-modal.component.scss'],
  standalone: false
})
export class TestWorkflowModalComponent implements OnInit, OnChanges {
  testAsClientData: {
    applicationId?: string;
    clientId?: string;
    contractorTypeId?: string;
    mapServiceId?: string;
  } = {};
  needs: {
    needsParcelDataSource: boolean;
    needsMapService: boolean;
    needsContractorType: boolean;
  } = {
    needsParcelDataSource: false,
    needsMapService: false,
    needsContractorType: false
  };
  testAsClientForm: UntypedFormGroup;
  noParcelDataSources: boolean = false;
  @Output() onTest: EventEmitter<{
    clientId?: string;
    contractorTypeId?: string;
    mapServiceId?: string;
  }> = new EventEmitter();
  @Output() onCancel: EventEmitter<void> = new EventEmitter();
  @Output() closed = new EventEmitter<void>(); // Added by Vipul on 30-07-2026

  testAsContractorTypes: any[] = [];
  @Input() workflowId: string;
  @Input() versionId: string;
  @Input() clientId: string;
  @Input() applicationId?: string;
  @Input() isOpen = false; // Added by Vipul on 30-07-2026
  showTestAsClient = false;

  testAsClients: DropdownOption[] = [];
  testAsMapServices: DropdownOption[] = [];
  // Added by Vipul on 30-07-2026
  modalData: CommonConfirmationModalData = {
    open: false,
    modalDescp: 'Test as Client',
    yesNoBtn: true,
    replaceYesWith: 'Test as Client',
    replaceNoWith: 'Cancel',
    modalWidth: '650px',
    customClass: 'test-workflow-modal',
    customCloseAccepted: true
  };
  // End by Vipul on 30-07-2026

  isLoading: boolean = false;
  appViewLoading: boolean = false;

  constructor(
    private _fb: UntypedFormBuilder,
    public _context: WorkflowContextService,
    private _workflowSvc: WorkflowService,
    private _clientSvc: ClientService,
    private _systemSvc: SystemService,
    private _contractorSvc: ContractorService,
    private router: Router
  ) {
    this.testAsClientData = {};
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Added by Vipul on 30-07-2026
    if (changes['isOpen']) {
      this.modalData = {
        ...this.modalData,
        open: this.isOpen
      };
    }
    // Added by Vipul on 30-07-2026
    if (changes['clientId'] && !changes['clientId'].isFirstChange()) {
      this.loadTestNeeds(); // Added by Vipul on 30-07-2026
      this.testAsClientData.clientId = this.clientId;
      if (
        this.testAsClientForm &&
        this.testAsClientForm.contains('testClient')
      ) {
        this.testAsClientForm.controls['testClient'].setValue(this.clientId);
      }
    }

    // Added by Vipul on 30-07-2026
    if (this.workflowId && this.versionId && changes['workflowId']) {
      this.loadTestNeeds();
    }
    // Added by Vipul on 30-07-2026
  }

  ngOnInit() {
    this.testAsClientForm = this._fb.group({});

    const clientId: string =
      this._context.testAsData?.clientId ||
      this.clientId ||
      this._context.client?.id;

    if (clientId) {
      this.testAsClientData.clientId = clientId;
    } else {
      this.showTestAsClient = true;
    }
  }

  loadTestNeeds() {
    this._workflowSvc
      .getTestApplicationNeeds(this.workflowId, this.versionId)
      .subscribe(needs => {
        this.testAsClientForm = this._fb.group({
          testClient: [null, Validators.required],
          testMapService: [
            null,
            needs.needsMapService ? Validators.required : []
          ],
          testContractorType: [
            null,
            needs.needsContractorType ? Validators.required : []
          ],
          testParcelDataSource: [
            null,
            needs.needsParcelDataSource ? Validators.required : []
          ]
        });

        this.needs = needs;

        this._clientSvc.getClients().subscribe(clients => {
          this.testAsClients = clients.map(c => ({
            label: c.name,
            value: String(c.id)
          }));

          if (this.testAsClientData.clientId) {
            this.testAsClientForm.controls['testClient'].setValue(
              String(this.testAsClientData.clientId)
            );
          }
        });

        this.testAsClientSelected(this.testAsClientData.clientId);
        // }
      });
  }

  cancelTest() {
    this.reset();
  }

  reset() {
    this.showTestAsClient = true;
    this.testAsClientData.contractorTypeId = null;
    this.testAsClientData.mapServiceId = null;
    this.needs = null;
    this.testAsClientData.clientId = this.clientId;
  }

  testAsClientSelected(clientId: string) {
    if (clientId) {
      if (this.needs.needsParcelDataSource) {
        this._systemSvc.hasParcelDataSource(clientId).subscribe(result => {
          this.noParcelDataSources = !result;
          if (this.noParcelDataSources) {
            this.testAsClientForm.controls['testParcelDataSource'].setValue('');
          } else {
            this.testAsClientForm.controls['testParcelDataSource'].setValue(
              'true'
            );
          }
        });
      }

      if (this.needs.needsMapService) {
        this._systemSvc
          .getMapServicesByClient(new Client({ id: clientId }))
          .subscribe(mapServices => {
            this.testAsMapServices = mapServices.map(ms => ({
              label: ms.name,
              value: ms.id
            }));

            if (this.testAsMapServices?.length === 1) {
              const id = this.testAsMapServices[0].value as string;

              this.testAsClientData.mapServiceId = id;
              this.testAsClientForm.controls['testMapService'].setValue(id);
            }
            if (
              this.testAsMapServices?.length === 0 ||
              !this.testAsMapServices
            ) {
              this.testAsClientData.mapServiceId = null;
            }
          });
      }

      if (this.needs.needsContractorType) {
        const searchOptions: ContractorTypeSearchOptions = new ContractorTypeSearchOptions();
        searchOptions.client = new Client({ id: clientId });

        this._contractorSvc
          .searchContractorTypes(searchOptions)
          .subscribe(contractorTypes => {
            this.testAsContractorTypes = contractorTypes.map(ct => ({
              label: ct.name,
              value: ct.id
            }));
          });
      }
    }
  }

  testWorkflow() {
    // Start Code added by Sanchit on 14/05/2026
    if (this.testAsClientForm?.invalid) {
      this.testAsClientForm.markAllAsTouched();
      return;
    }
    // End Code added by Sanchit on 14/05/2026

    // Capture the user's Test as Client selections into testAsClientData so
    // they can be persisted to the shared workflow context. Without this,
    // navigating to the test application would find no testAsData in context
    // (or stale testAsData from a prior test) and re-prompt the user for the
    // same selections. Mirrors WorkflowTestAsClientComponent.testAsClient().
    this.testAsClientData.clientId =
      this.testAsClientForm.controls['testClient']?.value ??
      this.testAsClientData.clientId;
    this.testAsClientData.mapServiceId =
      this.testAsClientForm.controls['testMapService']?.value ?? null;
    this.testAsClientData.contractorTypeId =
      this.testAsClientForm.controls['testContractorType']?.value ?? null;

    const parentRoute = this.router.routerState.snapshot.url;
    const segments = parentRoute
      .split('/')
      .slice(0, -4)
      .filter(Boolean);

    this.isLoading = true;

    this._workflowSvc
      .startApplication(this.workflowId, true, '1')
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          const startingActivityId = '1';
          const applicationId =
            response?.applicationId ??
            response?.applicationID ??
            response?.applicationld;

          if (!applicationId) {
            return;
          }

          // Stamp the new applicationId onto testAsClientData and persist it
          // to the shared context BEFORE navigating. The app view uses
          // testAsData.applicationId to recognize that the current test
          // application matches the prior Test as Client selections and
          // avoid re-opening this modal. Mirrors the persistence performed
          // in WorkflowDiagramComponent.testWorkflow() for the legacy flow.
          this.testAsClientData.applicationId = applicationId;
          this._context.testAsData$.next({ ...this.testAsClientData });

          // Added by Vipul on 30-07-2026
          const routePath = [
            '/',
            ...segments,
            'workflow-applications',
            'data',
            'application',
            applicationId
          ];

          this.router.navigate([...routePath]).then(() => {
            this.close();
            this.onTest.emit();
          });

          // End by Vipul on 30-07-2026
        },
        error: err => {
          if (err?.error) {
          }
        }
      });
  }

  // Added by Vipul on 30-07-2026
  show() {
    this.modalData = {
      ...this.modalData,
      open: true
    };
  }

  close() {
    this.modalData = {
      ...this.modalData,
      open: false
    };

    this.closed.emit();
  }
  // End by Vipul on 30-07-2026
}
